import { Component, inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { DatePipe } from '@angular/common';
import { ButtonComponent } from '../../shared/components/redesign/botao/botao.component';
import { CampoTextoComponent } from '../../shared/components/redesign/campo-texto/campo-texto.component';
import { CampoSelectComponent } from '../../shared/components/redesign/campo-select/campo-select.component';
import { CampoSegmentadoComponent, OpcaoSegmentada } from '../../shared/components/redesign/campo-segmentado/campo-segmentado.component';
import { ModalConfirmacaoComponent } from '../../shared/components/redesign/modal-confirmacao/modal-confirmacao.component';
import { FluxoService } from '../../shared/service/fluxo-service';
import { FluxoSelect } from '../../shared/model/fluxo/fluxo-select-model';
import { Setor } from '../../shared/model/setor/setor.model';
import { SetorService } from '../../shared/service/setor.service';
import { MsgService } from '../../shared/service/msg.service';
import { ContaService } from '../../shared/service/conta.service';
import { ValidacaoAlteracaoCampoService } from '../../shared/service/utilitarios/validacao-alteracao-campo.service';
import { Conta } from '../../shared/model/conta.model';
import { CanalOrigemContatoEnum } from '../../shared/enum/canal-origem-contato.enum';
import { ArrastarFecharSheetDirective } from '../../chat/utils/arrastar-fechar-sheet.directive';

@Component({
  selector: 'app-modal-cadastro-conta',
  standalone: true,
  imports: [
    ButtonComponent,
    CampoTextoComponent,
    CampoSelectComponent,
    CampoSegmentadoComponent,
    ReactiveFormsModule,
    MatIconModule,
    DatePipe,
    ArrastarFecharSheetDirective,
  ],
  templateUrl: './modal-cadastro-conta.component.html',
  styleUrl: './modal-cadastro-conta.component.css'
})
export class ModalCadastroContaComponent implements OnInit {
  readonly CanalOrigemContatoEnum = CanalOrigemContatoEnum;
  readonly opcoesAtivo: OpcaoSegmentada[] = [
    { valor: true, label: 'Ativo' },
    { valor: false, label: 'Inativo' },
  ];
  readonly opcoesTipoConta: OpcaoSegmentada[] = [
    { valor: CanalOrigemContatoEnum.WHATSAPP, label: 'WhatsApp', icone: './assets/logos/wpp-logo.png' },
    { valor: CanalOrigemContatoEnum.INSTAGRAM, label: 'Instagram', icone: './assets/logos/ig-logo-nova.svg' },
  ];

  readonly opcaoTodosSetor = { id: 0, nome: 'Todos' } as unknown as Setor;

  isEdicao = false;
  isVisualizacao = false;
  conectando = false;
  salvando = false;

  conta: Conta = new Conta();
  fluxos: FluxoSelect[] = [];
  setores: Setor[] = [];
  form!: FormGroup;

  get setoresComTodos(): Setor[] {
    return [this.opcaoTodosSetor, ...this.setores];
  }

  private readonly dialogRef = inject<MatDialogRef<ModalCadastroContaComponent>>(MatDialogRef);
  public data = inject(MAT_DIALOG_DATA);
  private readonly dialog = inject(MatDialog);
  private readonly fb = inject(FormBuilder);
  private readonly fluxoService = inject(FluxoService);
  private readonly setorService = inject(SetorService);
  private readonly msgService = inject(MsgService);
  private readonly contaService = inject(ContaService);
  private readonly alteracaoService = inject(ValidacaoAlteracaoCampoService);

  get isInstagram(): boolean {
    const tipo = this.isEdicao ? this.conta.canalOrigem : this.form?.get('tipoConta')?.value;
    return tipo === CanalOrigemContatoEnum.INSTAGRAM;
  }

  get conectado(): boolean {
    return !!this.conta.conectado;
  }

  get mostrarFluxoESetor(): boolean {
    return !this.isInstagram || this.conectado;
  }

  ngOnInit() {
    this.isVisualizacao = !!this.data?.['visualizar'];
    this.conta = this.data?.['conta'] ?? new Conta();
    this.isEdicao = !!this.conta.id;

    this.form = this.fb.group({
      tipoConta: [{ value: this.conta.canalOrigem ?? CanalOrigemContatoEnum.WHATSAPP, disabled: this.isEdicao }],
      nome: [this.conta.nome, [Validators.required]],
      numero: [this.conta.numero, [Validators.required]],
      whatsappBussinesId: [this.conta.whatsappBussinesId],
      numeroMetaId: [this.conta.numeroMetaId],
      aplicativoId: [this.conta.aplicativoId],
      clientSecret: [''],
      redirectUri: [this.conta.redirectUri || `${window.location.origin}/conta/instagram/callback`],
      fluxo: [this.conta.fluxo],
      setor: [this.setorInicial()],
      ativo: [this.conta.ativo ?? true],
    });

    this.aplicarModoCanal();
    this.form.get('tipoConta')!.valueChanges.subscribe(() => this.aplicarModoCanal());
    this.form.get('setor')!.valueChanges.subscribe((valor: Setor[]) => this.aplicarSelecaoTodosSetor(valor));

    this.alteracaoService.camposNaoAlterados();
    this.alteracaoService.monitorarAlteracoes(this.form, this.isEdicao ? [] : ['tipoConta']);

    this.buscarFluxos();
    this.buscarSetores();

    if (this.isVisualizacao) {
      this.form.disable({ emitEvent: false });
    }

    this.alteracaoService.registrarFechamentoComConfirmacao(this.dialogRef, 'Deseja realmente cancelar?');
  }

  private aplicarModoCanal() {
    const nome = this.form.get('nome')!;
    const numero = this.form.get('numero')!;
    const whatsappBussinesId = this.form.get('whatsappBussinesId')!;
    const numeroMetaId = this.form.get('numeroMetaId')!;

    if (this.isInstagram) {
      nome.disable({ emitEvent: false });
      numero.disable({ emitEvent: false });
      whatsappBussinesId.disable({ emitEvent: false });
      numeroMetaId.disable({ emitEvent: false });
      numero.clearValidators();
      numero.updateValueAndValidity({ emitEvent: false });
    } else {
      nome.enable({ emitEvent: false });
      numero.enable({ emitEvent: false });
      whatsappBussinesId.enable({ emitEvent: false });
      numeroMetaId.enable({ emitEvent: false });
      numero.setValidators([Validators.required]);
      numero.updateValueAndValidity({ emitEvent: false });
    }
  }

  private setorInicial(): Setor[] {
    const setor = this.conta.setor as unknown;
    if (!setor) {
      return [];
    }
    return Array.isArray(setor) ? setor : [setor as Setor];
  }

  private aplicarSelecaoTodosSetor(valor: Setor[]) {
    const temTodos = (valor ?? []).some(s => s?.id === this.opcaoTodosSetor.id);
    if (!temTodos) {
      return;
    }
    const semTodos = valor.filter(s => s?.id !== this.opcaoTodosSetor.id);
    const todosJaSelecionados = semTodos.length === this.setores.length;
    this.form.get('setor')!.setValue(todosJaSelecionados ? [] : [...this.setores], { emitEvent: false });
  }

  buscarFluxos() {
    this.fluxoService.buscarFluxoSelect().subscribe({
      next: res => {
        this.fluxos = res;
        const fluxoAtual = this.fluxos.find(f => f.id === this.conta.fluxo?.id);
        if (fluxoAtual) {
          this.form.get('fluxo')!.setValue(fluxoAtual, { emitEvent: false });
        }
      }
    });
  }

  buscarSetores() {
    this.setorService.buscarSetoresSelect().subscribe({
      next: (res: Setor[]) => {
        this.setores = res;
        const idsAtuais = this.setorInicial().map(s => s.id);
        const setoresAtuais = this.setores.filter(s => idsAtuais.includes(s.id));
        if (setoresAtuais.length) {
          this.form.get('setor')!.setValue(setoresAtuais, { emitEvent: false });
        }
      }
    });
  }

  get tituloModal(): string {
    if (this.isVisualizacao) {
      return 'Visualizar Conta';
    }
    return this.isEdicao ? 'Editar Conta' : 'Nova Conta';
  }

  get erroNome(): string {
    const c = this.form.get('nome');
    return c?.invalid && c?.touched ? 'Informe o nome da conta.' : '';
  }

  get erroNumero(): string {
    const c = this.form.get('numero');
    return c?.invalid && c?.touched ? 'Informe o número.' : '';
  }

  conectar() {
    const aplicativoId = this.form.get('aplicativoId')?.value;
    const clientSecret = this.form.get('clientSecret')?.value;
    const redirectUri = this.form.get('redirectUri')?.value;

    if (!aplicativoId || !clientSecret || !redirectUri) {
      this.form.get('aplicativoId')?.markAsTouched();
      this.form.get('clientSecret')?.markAsTouched();
      this.form.get('redirectUri')?.markAsTouched();
      this.msgService.msgAdvertencia('Preencha ID Aplicativo, Senha e Redirect URL antes de conectar.');
      return;
    }

    if (redirectUri.startsWith('http://')) {
      this.form.get('redirectUri')?.markAsTouched();
      this.msgService.msgAdvertencia('O Redirect URL precisa ser HTTPS — o Instagram recusa http:// mesmo em localhost.');
      return;
    }

    this.conectando = true;
    this.contaService.iniciarConexaoInstagram({
      contaId: this.conta.id,
      aplicativoId, clientSecret, redirectUri
    }).subscribe({
      next: res => {
        window.location.href = res.authUrl!;
      },
      error: err => {
        this.conectando = false;
        this.msgService.msgErro(err?.error?.message ?? 'Não foi possível iniciar a conexão com o Instagram.');
      }
    });
  }

  salvar() {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }
    if (this.isInstagram && !this.conectado) {
      this.msgService.msgAdvertencia('Conecte a conta do Instagram antes de salvar.');
      return;
    }

    const valores = this.form.getRawValue();
    const setores = ((valores.setor ?? []) as Setor[]).filter(s => s?.id !== this.opcaoTodosSetor.id);
    const ativo = !!valores.ativo;

    const payload = Object.assign(new Conta(), this.conta, {
      nome: valores.nome,
      numero: valores.numero,
      ativo,
      whatsappBussinesId: valores.whatsappBussinesId,
      aplicativoId: valores.aplicativoId,
      numeroMetaId: valores.numeroMetaId,
      fluxo: valores.fluxo,
      setor: setores,
      canalOrigem: this.isEdicao ? this.conta.canalOrigem : valores.tipoConta,
    });

    const precisaConfirmar = !this.isEdicao || this.alteracaoService.isCamposAlteracao();
    if (!precisaConfirmar) {
      this.dialogRef.close(payload);
      return;
    }

    const texto = this.isEdicao ? 'Deseja realmente editar esta conta?' : 'Deseja realmente salvar esta conta?';
    this.dialog.open(ModalConfirmacaoComponent, { data: { texto } }).afterClosed().subscribe(confirmou => {
      if (confirmou) {
        this.dialogRef.close(payload);
      }
    });
  }

  fechar() {
    this.alteracaoService.confirmarFechamento(this.dialogRef, 'Deseja realmente cancelar?');
  }

  cancelar() {
    this.alteracaoService.confirmarFechamento(this.dialogRef, 'Deseja realmente cancelar?');
  }
}
